import { useEffect, useRef, useState } from 'react';

interface UseAutoConfirmCountdownParams {
  /** 마운트 시점에 고정된다 — 이후 값이 바뀌어도 타이머를 재시작하지 않는다. */
  seconds: number;
  /** 카운트다운 종료 시 정확히 1회 호출된다. */
  onExpire: () => void;
}

interface UseAutoConfirmCountdownReturn {
  /** 표시용 남은 초. 0 은 반환하지 않는다 (3 → 2 → 1 → 만료). */
  secondsLeft: number;
}

/** 남은 시간을 다시 계산하는 주기. 탭 복귀 후 최대 이만큼만 늦게 보정된다. */
const TICK_INTERVAL_MS = 250;

/**
 * 남은 시간을 세다가 0 이 되면 확인 동작을 자동 실행하는 카운트다운.
 *
 * "N초 후 자동으로 이동" 류의 다이얼로그가 공유하는 UX 로직만 담는다 — 문구·다이얼로그·
 * 만료 후 행동은 호출부가 소유한다.
 *
 * @example
 * const { secondsLeft } = useAutoConfirmCountdown({
 *   seconds: 3,
 *   onExpire: () => { onConfirm(); onClose(); },
 * });
 */
export const useAutoConfirmCountdown = ({
  seconds,
  onExpire,
}: UseAutoConfirmCountdownParams): UseAutoConfirmCountdownReturn => {
  const [secondsLeft, setSecondsLeft] = useState(seconds);

  // deps 에 onExpire 를 넣으면 인라인 콜백일 때 매 틱 타이머가 재시작돼 영원히 만료되지
  // 않는다. 렌더 중이 아니라 커밋 후에 갱신해야 폐기된 렌더의 값이 남지 않는다.
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  });

  useEffect(() => {
    // 틱을 누적하지 않고 종료 시각을 고정한다 — 탭이 백그라운드로 가 타이머가 스로틀링돼도
    // 복귀 시 남은 시간을 다시 계산해 따라잡는다.
    const deadline = Date.now() + seconds * 1000;

    const interval = setInterval(() => {
      const left = Math.ceil((deadline - Date.now()) / 1000);

      if (left > 0) {
        // 같은 값이면 React 가 리렌더를 건너뛴다.
        setSecondsLeft(left);
        return;
      }

      // clearInterval 이 먼저다 — onExpire 가 하드 네비게이션이라 두 번 실행되면 안 된다.
      clearInterval(interval);
      setSecondsLeft(1);
      // updater 안이 아닌 콜백 본문이므로 사이드이펙트 호출이 안전하다.
      onExpireRef.current();
    }, TICK_INTERVAL_MS);

    // 확인 버튼·dim 클릭으로 먼저 닫히면 cleanup 이 타이머를 제거한다.
    return () => clearInterval(interval);
  }, [seconds]);

  return { secondsLeft };
};
